import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const accentThemes = {
  gobunnyy: {
    name: 'GoBunnyy',
    primary: '#000055',
    primaryLight: '#1a1a80',
    secondary: '#64748b',
    background: '#f1f5f9',
    surface: '#ffffff',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    navbar: '#ffffff',
    accent: '#000055',
  },
  ocean: {
    name: 'Ocean',
    primary: '#0369a1',
    primaryLight: '#0ea5e9',
    secondary: '#64748b',
    background: '#f1f5f9',
    surface: '#ffffff',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    navbar: '#ffffff',
    accent: '#06b6d4',
  },
  violet: {
    name: 'Violet',
    primary: '#5b21b6',
    primaryLight: '#8b5cf6',
    secondary: '#64748b',
    background: '#f1f5f9',
    surface: '#ffffff',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    navbar: '#ffffff',
    accent: '#7c3aed',
  },
  emerald: {
    name: 'Emerald',
    primary: '#047857',
    primaryLight: '#10b981',
    secondary: '#64748b',
    background: '#f1f5f9',
    surface: '#ffffff',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    navbar: '#ffffff',
    accent: '#0891b2',
  },
  sunset: {
    name: 'Sunset',
    primary: '#c2410c',
    primaryLight: '#f97316',
    secondary: '#64748b',
    background: '#f1f5f9',
    surface: '#ffffff',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    navbar: '#ffffff',
    accent: '#e11d48',
  },
};

const darkTheme = {
  primary: '#6b6bff',
  primaryLight: '#8888ff',
  secondary: '#94a3b8',
  background: '#0b1215',
  surface: '#0d0d2a',
  text: '#f1f5f9',
  textSecondary: '#94a3b8',
  border: '#1e1e3d',
  navbar: '#000055',
  accent: '#6b6bff',
};

const getSystemTheme = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const darkenHex = (hex, amount = 20) => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const r = Math.max(0, parseInt(normalized.slice(0, 2), 16) - amount);
  const g = Math.max(0, parseInt(normalized.slice(2, 4), 16) - amount);
  const b = Math.max(0, parseInt(normalized.slice(4, 6), 16) - amount);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const resolveStoredMode = (saved) => {
  if (!saved) return 'system';
  if (saved === 'system' || saved === 'light' || saved === 'dark') return saved;
  if (saved === 'dark') return 'dark';
  return 'light';
};

const resolveStoredAccent = (saved) => {
  const legacyMap = {
    blue: 'ocean',
    green: 'emerald',
    purple: 'violet',
    gobunny: 'gobunnyy',
  };
  const normalized = legacyMap[saved] || saved;
  if (!normalized || normalized === 'ui8' || normalized === 'light' || normalized === 'dark' || normalized === 'system') {
    return 'gobunnyy';
  }
  return accentThemes[normalized] ? normalized : 'gobunnyy';
};

const migrateLegacyTheme = () => {
  const legacy = localStorage.getItem('theme');
  if (!legacy) return;

  const hasMode = localStorage.getItem('themeMode');
  const hasAccent = localStorage.getItem('accentTheme');

  if (!hasMode) {
    if (legacy === 'dark') {
      localStorage.setItem('themeMode', 'dark');
    } else if (legacy === 'system') {
      localStorage.setItem('themeMode', 'system');
    } else {
      localStorage.setItem('themeMode', 'light');
    }
  }

  if (!hasAccent) {
    localStorage.setItem('accentTheme', resolveStoredAccent(legacy));
  }
};

const applyThemeVariables = (theme, isDark) => {
  const root = document.documentElement;
  const isLightNavbar = theme.navbar === '#ffffff' || theme.navbar.toLowerCase() === '#fff';
  const primaryLight = theme.primaryLight || (isDark ? '#8888ff' : '#1a1a80');
  const accent = theme.accent || '#7c3aed';

  root.style.setProperty('--primary-color', theme.primary);
  root.style.setProperty('--primary-hover', isDark ? theme.primary : darkenHex(theme.primary, 18));
  root.style.setProperty('--primary-shade', primaryLight);
  root.style.setProperty('--primary-light', primaryLight);
  root.style.setProperty(
    '--primary-tint',
    isDark
      ? `color-mix(in srgb, ${theme.primary} 18%, transparent)`
      : `color-mix(in srgb, ${theme.primary} 10%, #fff)`
  );
  root.style.setProperty(
    '--focus-ring',
    isDark
      ? `0 0 0 3px color-mix(in srgb, ${theme.primary} 28%, transparent)`
      : `0 0 0 3px color-mix(in srgb, ${theme.primary} 14%, transparent)`
  );
  root.style.setProperty('--primary-vivid', isDark ? primaryLight : theme.primary);
  root.style.setProperty('--secondary-color', theme.secondary);
  root.style.setProperty('--background-color', theme.background);
  root.style.setProperty('--bg-primary', theme.background);
  const glassSurface = isDark ? 'rgba(13, 13, 42, 0.68)' : 'rgba(255, 255, 255, 0.62)';
  const glassMuted = isDark ? 'rgba(11, 18, 21, 0.48)' : 'rgba(255, 255, 255, 0.38)';
  root.style.setProperty('--surface-color', glassSurface);
  root.style.setProperty('--bg-secondary', glassSurface);
  root.style.setProperty('--text-color', theme.text);
  root.style.setProperty('--text-primary', theme.text);
  root.style.setProperty('--text-heading', isDark ? theme.text : '#0b1215');
  root.style.setProperty('--text-secondary-color', theme.textSecondary);
  root.style.setProperty('--text-secondary', theme.textSecondary);
  root.style.setProperty('--border-color', theme.border);
  root.style.setProperty('--navbar-color', theme.navbar);
  root.style.setProperty(
    '--gradient-primary',
    `linear-gradient(135deg, ${theme.primary} 0%, ${primaryLight} 100%)`
  );
  root.style.setProperty(
    '--gradient-profile',
    `linear-gradient(135deg, ${theme.primary} 0%, ${primaryLight} 100%)`
  );
  root.style.setProperty('--accent-violet', accent);
  root.style.setProperty('--success-color', isDark ? '#34d399' : '#059669');
  root.style.setProperty('--info-color', isDark ? '#6b6bff' : '#000055');

  root.style.setProperty('--chart-1', theme.primary);
  root.style.setProperty('--chart-2', isDark ? '#8888ff' : '#1a1a80');
  root.style.setProperty('--chart-3', isDark ? '#4040aa' : '#011222');
  root.style.setProperty('--chart-4', isDark ? '#2a2a99' : '#000055');
  root.style.setProperty('--chart-5', isDark ? '#fbbf24' : '#d97706');
  root.style.setProperty('--chart-6', isDark ? '#fb7185' : '#e11d48');
  root.style.setProperty('--chart-7', isDark ? '#5555cc' : accent);
  root.style.setProperty('--chart-8', isDark ? '#2a2a55' : '#0d0d2a');
  root.style.setProperty('--dash-tone-1', theme.primary);
  root.style.setProperty('--dash-tone-2', isDark ? '#8888ff' : '#1a1a80');
  root.style.setProperty('--dash-tone-3', isDark ? '#4040aa' : '#011222');
  root.style.setProperty('--dash-tone-4', isDark ? '#2a2a99' : '#000055');
  root.style.setProperty('--dash-tone-5', isDark ? '#fbbf24' : '#d97706');
  root.style.setProperty('--dash-tone-6', isDark ? '#fb7185' : '#e11d48');
  root.style.setProperty('--dash-tone-7', isDark ? '#5555cc' : accent);
  root.style.setProperty('--dash-tone-8', isDark ? '#2a2a55' : '#0d0d2a');

  const mesh = isDark
    ? `linear-gradient(168deg, ${theme.background} 0%, ${theme.surface} 50%, ${theme.background} 100%)`
    : `linear-gradient(165deg, #f1f5f9 0%, #e8eaf5 42%, #f4f5fb 100%)`;

  root.style.setProperty('--mesh-gradient', mesh);

  if (isDark) {
    root.style.setProperty('--workspace-canvas', '#0b1215');
    root.style.setProperty('--workspace-surface', '#0d0d2a');
    root.style.setProperty('--workspace-border', '#1e1e3d');
    root.style.setProperty('--workspace-border-strong', '#2a2a55');
    root.style.setProperty('--glass-bg', 'rgba(13, 13, 42, 0.55)');
    root.style.setProperty('--glass-bg-strong', 'rgba(13, 13, 42, 0.72)');
    root.style.setProperty('--glass-bg-muted', 'rgba(11, 18, 21, 0.5)');
    root.style.setProperty('--glass-panel-bg', 'rgba(13, 13, 42, 0.72)');
    root.style.setProperty('--glass-panel-border', 'rgba(255, 255, 255, 0.08)');
    root.style.setProperty('--glass-sidebar-bg', 'rgba(11, 18, 21, 0.65)');
    root.style.setProperty('--glass-filter-bg', 'rgba(11, 18, 21, 0.5)');
    root.style.setProperty('--glass-table-header', 'rgba(11, 18, 21, 0.55)');
    root.style.setProperty('--surface-muted', glassMuted);
    root.style.setProperty('--border-light', '#0d0d2a');
    root.style.setProperty('--chrome-panel-bg', '#0d0d2a');
    root.style.setProperty('--chrome-panel-muted', '#0b1215');
    root.style.setProperty('--chrome-panel-hover', '#1e1e3d');
    root.style.setProperty('--chrome-btn-bg', 'rgba(255, 255, 255, 0.1)');
  } else {
    root.style.setProperty('--workspace-canvas', theme.background || '#f4f5f7');
    root.style.setProperty('--workspace-surface', '#ffffff');
    root.style.setProperty('--workspace-border', theme.border || '#e5e7eb');
    root.style.setProperty('--workspace-border-strong', '#d1d5db');
    root.style.setProperty('--glass-bg', 'rgba(255, 255, 255, 0.5)');
    root.style.setProperty('--glass-bg-strong', 'rgba(255, 255, 255, 0.62)');
    root.style.setProperty('--glass-bg-muted', 'rgba(255, 255, 255, 0.38)');
    root.style.setProperty('--glass-panel-bg', 'rgba(255, 255, 255, 0.62)');
    root.style.setProperty('--glass-panel-border', 'rgba(255, 255, 255, 0.55)');
    root.style.setProperty('--glass-sidebar-bg', 'rgba(255, 255, 255, 0.52)');
    root.style.setProperty('--glass-filter-bg', 'rgba(255, 255, 255, 0.38)');
    root.style.setProperty('--glass-table-header', 'rgba(244, 245, 247, 0.7)');
    root.style.setProperty('--surface-muted', glassMuted);
    root.style.setProperty('--border-light', '#f3f4f6');
    root.style.setProperty('--chrome-panel-bg', '#ffffff');
    root.style.setProperty('--chrome-panel-muted', '#f4f5f7');
    root.style.setProperty('--chrome-panel-hover', '#eef0f3');
    root.style.setProperty('--chrome-btn-bg', '#eef0f3');
  }

  root.dataset.navbarStyle = isLightNavbar ? 'light' : 'dark';
};

export const ThemeProvider = ({ children }) => {
  migrateLegacyTheme();

  const [themeMode, setThemeMode] = useState(() =>
    resolveStoredMode(localStorage.getItem('themeMode'))
  );
  const [accentTheme, setAccentTheme] = useState(() =>
    resolveStoredAccent(localStorage.getItem('accentTheme'))
  );
  const [resolvedTheme, setResolvedTheme] = useState('light');

  const getEffectiveTheme = useCallback(() => {
    if (themeMode === 'system') return getSystemTheme();
    return themeMode;
  }, [themeMode]);

  useEffect(() => {
    const root = document.documentElement;
    const effective = getEffectiveTheme();
    setResolvedTheme(effective);

    if (themeMode === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', themeMode);
    }

    const isDark = effective === 'dark';
    const theme = isDark ? darkTheme : accentThemes[accentTheme] || accentThemes.gobunnyy;
    applyThemeVariables(theme, isDark);

    localStorage.setItem('themeMode', themeMode);
    localStorage.setItem('accentTheme', accentTheme);
    localStorage.setItem('theme', isDark ? 'dark' : accentTheme);
  }, [themeMode, accentTheme, getEffectiveTheme]);

  useEffect(() => {
    if (themeMode !== 'system') return undefined;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const effective = media.matches ? 'dark' : 'light';
      setResolvedTheme(effective);
      const theme = effective === 'dark' ? darkTheme : accentThemes[accentTheme] || accentThemes.gobunnyy;
      applyThemeVariables(theme, effective === 'dark');
    };

    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [themeMode, accentTheme]);

  const setColorMode = (mode) => setThemeMode(mode);
  const changeAccentTheme = (name) => {
    if (accentThemes[name]) setAccentTheme(name);
  };

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        accentTheme,
        resolvedTheme,
        accentThemes,
        themes: accentThemes,
        currentTheme: accentTheme,
        setColorMode,
        changeTheme: changeAccentTheme,
        changeAccentTheme,
        isDark: resolvedTheme === 'dark',
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
